import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Flex,
  GoodsCard,
  GoodsCardSkeletonList,
  List,
  useScreenSize,
} from '@banhala/ui-kit';
import { useFocusEffect } from '@react-navigation/native';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { AnalyticsScreen } from 'shared/src/context';
import { useExperimentGroupValue } from 'shared/src/context/UserContext/useExperimentGroup';
import { useWishlistSelectOptionBottomSheet } from 'goods/src/pages/GoodsDetail/components/SelectOptionBottomSheet/WishlistSelectOptionBottomSheet';
import type { SelectOptionConfirm } from 'goods/src/pages/GoodsDetail/components/SelectOptionBottomSheet/SelectOptionBottomSheet';
import { useWishlistPostGoodsActionBottomSheet } from 'goods/src/pages/GoodsDetail/components/PostGoodsActionBottomSheet/WishlistPostGoodsActionBottomSheet';
import { queryKey } from 'goods/src/pages/GoodsDetail/constant/queryKey';
import { bridge, isWeb, useAsyncEffect, useNavigator } from 'shared/src/utils';
import {
  useAddLikeGoods,
  useBottomSheetRoute,
  useIsForegroundNow,
  useIsInFragmentScreens,
  useLikeMap,
} from 'shared/src/hooks';
import { useSafeTimeout } from 'shared/src/hooks/useSafeTimeout';
import { useSyncLocalGoodsLike } from 'shared/src/hooks/useSyncLocalGoodsLike';
import { useMyInfo, useRemoveLikeGoods } from 'shared/src/queries';
import { cartService, type LikedGoodsPrimaryCardEntity } from 'shared/src/api';
import { useLikedGoods } from '../../queries';
import { likeGoodsFilterAtom } from '../../states/likeGoodsFilter.atom';
import { AiFittingButton } from './components/AiFittingButton';
import { CategoryFilterRow } from './components/CategoryFilterRow';
import { CommunityBanner } from './components/CommunityBanner';
import { DeliveryFilterEmpty } from './components/DeliveryFilterEmpty';
import { LikedGoodsCountHeader } from './components/LikedGoodsCountHeader';
import { LikedGoodsEmpty } from './components/LikedGoodsEmpty';
import { LikedGoodsLoginBanner } from './components/LikedGoodsLoginBanner';
import { PrimaryCardItem } from './components/PrimaryCardItem';
import { LikeScrollToTopButton } from '../LikeScrollToTopButton';
import { useScrollToTop } from '../../hooks/useScrollToTop';
import { WishlistCartButton } from './components/WishlistCartButton';
import { AI_FITTING_ROOMS_ZZIM_DEEPLINK } from './constants/aiFittingRoomsZzim';
import { useLogClickAddToCartEntry } from './hooks/useLogClickAddToCartEntry';
import { useLogClickEditOn } from './hooks/useLogClickEditOn';
import { useLogClickGoods } from './hooks/useLogClickGoods';
import { useLogDisplayGoods } from './hooks/useLogDisplayGoods';
import { useLogRefreshScreen } from './hooks/useLogRefreshScreen';
import { useLogViewLikedGoods } from './hooks/useLogViewLikedGoods';
import {
  convertLikedGoodsItemToGoodsData,
  type LikedGoodsCardItem,
} from './utils/convertLikedGoodsItemToGoodsData';

const SCREEN = 'LIKED_GOODS';

const COLUMN_GAP = 2;
const COLUMNS = 3;

const VIEWABILITY_CONFIG = {
  itemVisiblePercentThreshold: 50,
  minimumViewTime: 500,
};

/** 3-col 그리드 슬롯 — 상품 또는 프로모션 카드 */
type GridSlot =
  | { type: 'goods'; entry: LikedGoodsCardItem }
  | { type: 'primary'; entity: LikedGoodsPrimaryCardEntity };

/**
 * 한 List에 헤더 + 상품을 함께 넣고 카테고리 필터만 sticky로 만들기 위한
 * row 단위 데이터.
 */
export type LikeGoodsRow =
  | { type: 'loginBanner'; key: 'loginBanner' }
  | { type: 'filter'; key: 'filter' }
  | { type: 'aiFitting'; key: 'aiFitting' }
  | { type: 'count'; key: 'count'; totalCount: number }
  | { type: 'empty'; key: 'empty'; reason: 'delivery' | 'no-likes' }
  | { type: 'gridRow'; key: string; slots: GridSlot[] }
  | {
      type: 'communityBanner';
      key: 'communityBanner';
      goodsSno: number;
      imageUrl: string;
    };

interface LikeGoodsProps {
  isActive: boolean;
}

export const LikeGoods = ({ isActive }: LikeGoodsProps) => {
  return (
    <AnalyticsScreen screen={SCREEN}>
      <LikeGoodsContent isActive={isActive} />
    </AnalyticsScreen>
  );
};

const LikeGoodsContent = ({ isActive }: LikeGoodsProps) => {
  useLogViewLikedGoods(isActive);
  const { navigator } = useNavigator();
  const { width: screenWidth } = useScreenSize();
  const itemWidth = (screenWidth - COLUMN_GAP * (COLUMNS - 1)) / COLUMNS;

  const filter = useAtomValue(likeGoodsFilterAtom);
  const { data: myInfo } = useMyInfo();
  const isAnonymous = myInfo?.is_anonymous ?? false;
  const queryClient = useQueryClient();

  const filterParams = useMemo(
    () => ({
      is_fast_delivery: filter.isDeliveryFilterOn,
      category_sno: filter.selectedCategorySno ?? undefined,
      order: filter.sortKey ?? undefined,
    }),
    [filter.isDeliveryFilterOn, filter.selectedCategorySno, filter.sortKey],
  );

  const { data, fetchNextPage, hasNextPage, isFetching, isLoading, refetch } =
    useLikedGoods(filterParams);

  const list = useMemo<LikedGoodsCardItem[]>(
    () => data?.item_list.map(convertLikedGoodsItemToGoodsData) ?? [],
    [data?.item_list],
  );

  // 상품 사이에 프로모션 카드를 정해진 index 위치에 인터리브
  const interleavedSlots = useMemo<GridSlot[]>(() => {
    const slots: GridSlot[] = list.map(entry => ({ type: 'goods', entry }));
    const primaries = data?.primary_card_list ?? [];
    // index 기준 정렬 후 앞에서부터 splice
    [...primaries]
      .sort((a, b) => a.index - b.index)
      .forEach(({ entity, index }) => {
        const insertAt = Math.min(Math.max(index, 0), slots.length);
        slots.splice(insertAt, 0, { type: 'primary', entity });
      });
    return slots;
  }, [list, data?.primary_card_list]);

  // RemoteConfig 미연동 — fallback 딥링크가 비어있지 않으면 노출 (iOS 동작과 매칭).
  const showAiFitting = AI_FITTING_ROOMS_ZZIM_DEEPLINK.length > 0;

  const [lastSelectedGoods, setLastSelectedGoods] = useState<{
    goodsSno: number;
    imageUrl: string;
  } | null>(null);
  const bannerScheduledRef = useRef(false);
  const bannerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const setSafeTimeout = useSafeTimeout();

  const resetBanner = useCallback(() => {
    if (bannerTimerRef.current != null) {
      clearTimeout(bannerTimerRef.current);
      bannerTimerRef.current = null;
    }
    setLastSelectedGoods(null);
    bannerScheduledRef.current = false;
  }, []);

  // 필터 변경 시 배너 초기화
  useEffect(() => {
    resetBanner();
  }, [
    resetBanner,
    filter.selectedCategorySno,
    filter.isDeliveryFilterOn,
    filter.sortKey,
  ]);

  const rows = useMemo<LikeGoodsRow[]>(() => {
    const result: LikeGoodsRow[] = [];

    if (isAnonymous) {
      result.push({ type: 'loginBanner', key: 'loginBanner' });
    }

    const isEmpty = list.length === 0 && !isLoading && !isFetching;
    const hideFilters =
      isEmpty &&
      filter.selectedCategorySno == null &&
      !filter.isDeliveryFilterOn;

    if (!hideFilters) {
      result.push({ type: 'filter', key: 'filter' });
      if (showAiFitting) {
        result.push({ type: 'aiFitting', key: 'aiFitting' });
      }
      result.push({
        type: 'count',
        key: 'count',
        totalCount: data?.total_count ?? 0,
      });
    }

    if (isEmpty) {
      result.push({
        type: 'empty',
        key: 'empty',
        reason: filter.isDeliveryFilterOn ? 'delivery' : 'no-likes',
      });
      return result;
    }

    // 배너가 들어갈 chunk 인덱스 (탭한 상품이 속한 행 다음)
    const bannerSlotIdx =
      !isAnonymous && lastSelectedGoods != null
        ? interleavedSlots.findIndex(
            slot =>
              slot.type === 'goods' &&
              slot.entry.item.sno === lastSelectedGoods.goodsSno,
          )
        : -1;
    const bannerAfterChunkIdx =
      bannerSlotIdx >= 0 ? Math.floor(bannerSlotIdx / COLUMNS) : null;

    for (let i = 0; i < interleavedSlots.length; i += COLUMNS) {
      const chunkIdx = i / COLUMNS;
      const chunk = interleavedSlots.slice(i, i + COLUMNS);
      const firstKeyPart = chunk[0];
      const keySuffix =
        firstKeyPart.type === 'goods'
          ? `g-${firstKeyPart.entry.item.sno}`
          : `p-${i}`;
      result.push({ type: 'gridRow', key: `row-${keySuffix}`, slots: chunk });
      if (bannerAfterChunkIdx === chunkIdx && lastSelectedGoods != null) {
        result.push({
          type: 'communityBanner',
          key: 'communityBanner',
          goodsSno: lastSelectedGoods.goodsSno,
          imageUrl: lastSelectedGoods.imageUrl,
        });
      }
    }
    return result;
  }, [
    interleavedSlots,
    list.length,
    data?.total_count,
    filter.selectedCategorySno,
    filter.isDeliveryFilterOn,
    isLoading,
    isFetching,
    isAnonymous,
    showAiFitting,
    lastSelectedGoods,
  ]);

  // useLikeMap의 initialLikeMap은 mount 시 한 번만 적용되므로,
  // list가 갱신될 때(다음 페이지/필터 변경)는 effect로 직접 setLikeMap 동기화.
  const { likeMap, setLikeMap } = useLikeMap();
  useEffect(() => {
    setLikeMap(prev => {
      const next = { ...prev };
      list.forEach(entry => {
        if (next[entry.item.sno] === undefined) {
          next[entry.item.sno] = entry.item.like?.is_liked ?? true;
        }
      });
      return next;
    });
  }, [list, setLikeMap]);

  useSyncLocalGoodsLike(setLikeMap);

  useAsyncEffect(async () => {
    if (!isActive) return;
    const localGoodsLike = await bridge.getLocalGoodsLike();
    setLikeMap(prev => ({ ...prev, ...localGoodsLike }));
  }, [isActive]);

  // 다른 화면에서 찜한 새 카테고리 반영: 포그라운드 복귀 시 카테고리 필터 재요청.
  const isForegroundNow = useIsForegroundNow();
  useEffect(() => {
    if (isForegroundNow) {
      queryClient.invalidateQueries({ queryKey: ['likedGoodsCategories'] });
    }
  }, [isForegroundNow, queryClient]);

  // 세션 변경(로그인/로그아웃) 시 likedGoods 캐시 무효화 → 재요청.
  // 첫 마운트는 제외하고 isAnonymous 변경된 케이스만.
  const prevAnonymousRef = useRef(isAnonymous);
  useEffect(() => {
    if (prevAnonymousRef.current === isAnonymous) return;
    prevAnonymousRef.current = isAnonymous;
    queryClient.invalidateQueries({ queryKey: ['likedGoods'] });
  }, [isAnonymous, queryClient]);

  const { addLikeGoods } = useAddLikeGoods({
    onChangeIsLiked: (sno, nextValue) => {
      setLikeMap(prev => ({ ...prev, [sno]: nextValue }));
    },
    screen: SCREEN,
  });

  const { mutateAsync: mutateRemoveLikeGoods } = useRemoveLikeGoods();

  const logClickGoods = useLogClickGoods();
  const logClickAddToCartEntry = useLogClickAddToCartEntry();
  const logClickEditOn = useLogClickEditOn();

  // 필터 변경 + 화면 재진입(focus) 시 display 로그 중복 방지 Set 초기화.
  const [focusBump, setFocusBump] = useState(0);
  useFocusEffect(
    useCallback(() => {
      setFocusBump(b => b + 1);
    }, []),
  );
  const filterResetKey = `${filter.selectedCategorySno ?? 'all'}-${filter.sortKey}-${filter.isDeliveryFilterOn}-f${focusBump}`;
  const logDisplayGoods = useLogDisplayGoods({ resetKey: filterResetKey });

  const cartFunnelGroup = useExperimentGroupValue({ sno: 'EXP1768' });
  const showCartButton = cartFunnelGroup === 'B' || cartFunnelGroup === 'C';
  const { show: showWishlistOptionBottomSheet } =
    useWishlistSelectOptionBottomSheet();
  const { show: showWishlistPostGoodsActionBottomSheet } =
    useWishlistPostGoodsActionBottomSheet();
  const shownRecommendSnosRef = useRef(new Set<number>());
  const { open: openBottomSheetRoute } = useBottomSheetRoute();
  const isInFragmentScreens = useIsInFragmentScreens();

  const handlePressItem = (entry: LikedGoodsCardItem) => {
    logClickGoods(entry);
    const { item } = entry;
    navigator.moveToGoods({
      sno: item.sno,
      firstPageRendering: item.first_page_rendering ?? null,
      remoteDeeplink: item.remote_deeplink ?? undefined,
      loggingAnalytics: entry.logging.analytics,
    });

    if (!isAnonymous && !bannerScheduledRef.current) {
      bannerScheduledRef.current = true;
      bannerTimerRef.current = setSafeTimeout(() => {
        bannerTimerRef.current = null;
        setLastSelectedGoods({ goodsSno: item.sno, imageUrl: item.image });
      }, 1000);
    }
  };

  const handlePressCart = async (entry: LikedGoodsCardItem) => {
    logClickAddToCartEntry(entry);
    // B군: as_bottom_sheet 딥링크로 네이티브 90% PDP 미리보기 바텀시트에 위임.
    if (cartFunnelGroup === 'B') {
      const { item } = entry;
      navigator.moveToGoods({
        sno: item.sno,
        firstPageRendering: item.first_page_rendering ?? null,
        remoteDeeplink: item.remote_deeplink ?? undefined,
        loggingAnalytics: entry.logging.analytics,
        asBottomSheet: true,
      });
      return;
    }
    const optionParams = {
      goodsId: entry.item.sno,
      mode: 'purchase' as const,
      isShowOnce: false,
      selectedOptions: [],
      updateSelectedOption: () => {},
      fromButton: 'LIKED_GOODS_CARD_CART_BUTTON',
      // PDP route 밖이라 옵션 시트가 PDP 상세 API 없이 카드 데이터로 로깅하도록 주입.
      logging: {
        screen: SCREEN,
        folderSno: entry.item.like.folder_sno,
        marketSno: entry.item.market_sno,
        deliveryType: entry.item.delivery_type,
      },
    };
    // 찜탭(LikeFragment)은 네이티브 탭바 호스팅이라 in-tree 바텀시트가 탭바에 가린다.
    // BottomSheetHost modal surface 경유로 띄워 탭바 위에 표시.
    const result = isInFragmentScreens
      ? await openBottomSheetRoute<typeof optionParams, SelectOptionConfirm>(
          'wishlist-select-option',
          optionParams,
        )
      : await showWishlistOptionBottomSheet(optionParams);
    if (result.type === 'confirm' && result.data.lastAction === 'addToCart') {
      await showCartRecommendOrToast(entry);
    }
  };

  const showCartRecommendOrToast = async (entry: LikedGoodsCardItem) => {
    const sno = entry.item.sno;
    if (shownRecommendSnosRef.current.has(sno)) {
      bridge.toast('장바구니에 상품을 담았어요');
      return;
    }
    const recommend = await queryClient
      .fetchQuery({
        queryKey: queryKey.goodsCartRecommend(sno),
        queryFn: () => cartService.getAddCartRecommend(sno),
      })
      .catch(() => undefined);
    if ((recommend?.carousels.length ?? 0) === 0) {
      bridge.toast('장바구니에 상품을 담았어요');
      return;
    }
    shownRecommendSnosRef.current.add(sno);
    const landingDeeplink = isWeb
      ? 'ably://webview?url=https://m.a-bly.com/cart'
      : 'ably://webview?title_bar=false&url=cart%3Ffrom%3DRECOMMENDEDMODAL';
    await showWishlistPostGoodsActionBottomSheet({
      goodsId: sno,
      logging: {
        screen: SCREEN,
        folderSno: entry.item.like.folder_sno,
        marketSno: entry.item.market_sno,
        deliveryType: entry.item.delivery_type,
      },
      mode: 'cart',
      title: { highlight: '장바구니', text: '에 상품을 담았어요' },
      image: entry.item.image,
      action: { text: '바로가기', deeplink: landingDeeplink },
    });
  };

  const handlePressLike = async (entry: LikedGoodsCardItem) => {
    const { item, logging } = entry;
    const isCurrentlyLiked = likeMap[item.sno] ?? false;
    if (isCurrentlyLiked) {
      await mutateRemoveLikeGoods({ sno: item.sno });
      bridge.likeGoods({ goods_sno: item.sno, is_liked: false });
      setLikeMap(prev => ({ ...prev, [item.sno]: false }));
      // 캐시에 남은 stale 데이터로 인한 재노출 방지.
      // refetchType: 'none' → 현재 화면은 회색 하트로 유지.
      queryClient.invalidateQueries({
        queryKey: ['likedGoods'],
        refetchType: 'none',
      });
      return;
    }
    addLikeGoods(item.sno, logging.analytics, logging.ably_only_log);
  };

  const logRefreshScreen = useLogRefreshScreen();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const handleRefresh = useCallback(async () => {
    logRefreshScreen();
    setIsRefreshing(true);
    resetBanner();
    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  }, [logRefreshScreen, refetch, resetBanner]);

  const { listRef, scrollY, handleScroll, handleScrollToTop } =
    useScrollToTop<LikeGoodsRow>();

  const showFooterSkeleton = hasNextPage || isLoading;

  return (
    <Flex flex={1}>
      <List<LikeGoodsRow>
        ref={listRef}
        data={rows}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        keyExtractor={row => row.key}
        // 비회원 배너가 있으면 index 0이 loginBanner라 sticky 하지 않음.
        // 그 경우 index 1(=filter)이 sticky.
        stickyHeaderIndices={isAnonymous ? [1] : [0]}
        onEndReachedThreshold={1}
        onEndReached={() => {
          if (hasNextPage && !isFetching) fetchNextPage();
        }}
        refreshing={isRefreshing}
        onRefresh={handleRefresh}
        viewabilityConfig={VIEWABILITY_CONFIG}
        onViewableItemsChanged={logDisplayGoods}
        ListFooterComponent={
          showFooterSkeleton ? (
            <GoodsCardSkeletonList
              count={6}
              numColumns={COLUMNS}
              bottomContentHeight={80}
            />
          ) : null
        }
        renderItem={({ item: row }) => {
          if (row.type === 'loginBanner') return <LikedGoodsLoginBanner />;
          if (row.type === 'filter') {
            return <CategoryFilterRow disabled={isFetching} />;
          }
          if (row.type === 'aiFitting') return <AiFittingButton />;
          if (row.type === 'count') {
            return (
              <LikedGoodsCountHeader
                totalCount={row.totalCount}
                isLoading={isLoading}
                onPressEdit={() => {
                  logClickEditOn();
                  navigator.moveToEditGoodsLike({
                    categorySno: filter.selectedCategorySno,
                    isDeliveryFilterOn: filter.isDeliveryFilterOn,
                    order: filter.sortKey,
                  });
                }}
              />
            );
          }
          if (row.type === 'empty') {
            return row.reason === 'delivery' ? (
              <DeliveryFilterEmpty />
            ) : (
              <LikedGoodsEmpty />
            );
          }
          if (row.type === 'communityBanner') {
            return (
              <CommunityBanner
                goodsSno={row.goodsSno}
                imageUrl={row.imageUrl}
              />
            );
          }
          if (row.type !== 'gridRow') return null;
          return (
            <Flex.Row gap={COLUMN_GAP}>
              {row.slots.map((slot, idx) => (
                <Flex
                  key={
                    slot.type === 'goods'
                      ? `g-${slot.entry.item.sno}`
                      : `p-${idx}`
                  }
                  flex={1}
                >
                  {slot.type === 'goods' ? (
                    <>
                      <GoodsCard
                        data={slot.entry}
                        size={'medium'}
                        defaultWidth={itemWidth}
                        isLike={likeMap[slot.entry.item.sno]}
                        onPress={() => handlePressItem(slot.entry)}
                        onPressLike={async () => {
                          await handlePressLike(slot.entry);
                        }}
                        testID={`goods/${slot.entry.item.sno}?LIST_ID=${slot.entry.item.like.referrer?.list_id}&LIST_INDEX=${slot.entry.item.like.referrer?.list_index}&DELIVERY_TYPE=${slot.entry.deliveryType}`}
                      />
                      {showCartButton && (
                        <Flex padding={{ top: 8, horizontal: 4, bottom: 16 }}>
                          <WishlistCartButton
                            onPress={() => handlePressCart(slot.entry)}
                          />
                        </Flex>
                      )}
                    </>
                  ) : (
                    <PrimaryCardItem entity={slot.entity} width={itemWidth} />
                  )}
                </Flex>
              ))}
              {Array.from({ length: COLUMNS - row.slots.length }).map(
                (_, i) => (
                  <Flex key={`pad-${i}`} flex={1} />
                ),
              )}
            </Flex.Row>
          );
        }}
      />
      <LikeScrollToTopButton scrollY={scrollY} onPress={handleScrollToTop} />
    </Flex>
  );
};
